import { notification } from "antd";
import { fetchAuthSession, getCurrentUser } from "aws-amplify/auth";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { RouterProvider } from "react-router-dom";

import notifySound from "../assets/sounds/notification.mp3";
import { messageNotify } from "../components/Chat/constants";
import { router } from "../util/library/Router";
import { selectUserId, selectUsername } from "../util/store/selectors";
import { getStore, useSelector } from "../util/store/store";
import { showErrorResponse } from "../util/varibles/global";
import { SOCKET_FUNC, SOCKET_NAME } from "../web-socket/constants";
import WSService from "../web-socket/web-socket-service";
import { loginActions } from "./LoginPage/reducer";
import { MESSAGE_TYPE } from "./MessagePage/constants";
import { messageActions } from "./MessagePage/reducer";

const audio = new Audio();

audio.src = notifySound;
audio.preload = "auto";

const playNotifySound = async () => {
    try {
        if (!audio.src) return;

        audio.currentTime = 0;
        await audio.play();
    } catch (e) {
        console.warn("Cannot play notification sound:", e);
    }
};

const enableNotifySound = async () => {
    try {
        if (!audio.src) return;

        audio.currentTime = 0;
        await audio.play();

        audio.pause();
        audio.currentTime = 0;
    } catch (e) {
        console.warn("Cannot enable notification sound:", e);
    }
};

const InitPage = () => {
    const dispatch = useDispatch();
    const userId = useSelector(selectUserId);
    const [notify, contextHolder] = notification.useNotification();

    useEffect(() => {
        const enable = async () => {
            await enableNotifySound();
            window.removeEventListener("click", enable);
        };

        window.addEventListener("click", enable);

        const processMessage = (value: any) => {
            switch (value.function) {
                case SOCKET_FUNC.SEEN_MESSAGE: {
                    dispatch(messageActions.seenMessage(value.message));
                    break;
                }

                case SOCKET_FUNC.CHAT: {
                    const { is_edited, message_type } = value.message.message;
                    const { id = null } = value.message || {};

                    if (id !== userId) {
                        playNotifySound();
                    }

                    let content;
                    const currentUser = selectUsername(getStore());

                    switch (message_type) {
                        case MESSAGE_TYPE.ADD: {
                            dispatch(messageActions.addChat(value.message));
                            content = messageNotify(
                                value.message,
                                () => dispatch(messageActions.readMessage(value.message)),
                                currentUser
                            );
                            break;
                        }

                        case MESSAGE_TYPE.DELETE: {
                            dispatch(messageActions.kickUser(value.message));
                            content = messageNotify(
                                value.message,
                                () => dispatch(messageActions.readMessage(value.message)),
                                currentUser
                            );
                            break;
                        }

                        case MESSAGE_TYPE.LEAVE: {
                            const { username } = value.message.message;

                            if (username === currentUser) {
                                const { type } = value.message;
                                const { id } = value.message.group_chat;

                                dispatch(
                                    messageActions.leaveChat({
                                        chatType: type,
                                        chatId: id,
                                    })
                                );
                            } else {
                                dispatch(messageActions.kickUser(value.message));
                                content = messageNotify(
                                    value.message,
                                    () => dispatch(messageActions.readMessage(value.message)),
                                    currentUser
                                );
                            }
                            break;
                        }

                        default: {
                            if (is_edited) {
                                const { id } = value.message.group_chat;

                                dispatch(
                                    messageActions.updateMessage({
                                        ...value.message,
                                        group_id: id,
                                    })
                                );

                                content = messageNotify(
                                    value.message,
                                    () => dispatch(messageActions.readMessage(value.message)),
                                    currentUser,
                                    true
                                );
                            } else {
                                dispatch(messageActions.addMessage(value.message));
                                content = messageNotify(
                                    value.message,
                                    () => dispatch(messageActions.readMessage(value.message)),
                                    currentUser
                                );
                            }
                        }
                    }

                    if (content) {
                        notify.info(content);
                    }

                    break;
                }

                case SOCKET_FUNC.UPDATE_TENANT: {
                    dispatch(loginActions.updateTenant(value.message));
                    break;
                }

                case SOCKET_FUNC.OPERATION_CONFIRMED:
                case SOCKET_FUNC.OPERATION_STATUS_CHANGE:
                case SOCKET_FUNC.OPERATION_STATE_CHANGE: {
                    const operations = value.message || [];

                    if (Array.isArray(operations)) {
                        dispatch(loginActions.updateOpsOfVessel(operations));
                    }

                    break;
                }
            }
        };

        let socket: WSService | undefined;

        const openWs = () => {
            if (userId) {
                socket = new WSService({
                    groups: ["global"],
                    user: { user_id: userId },
                    listener: {
                        lev: SOCKET_NAME.GLOBAL,
                        func: (value: any) => processMessage(value),
                    },
                    renew: true,
                });
            }
        };

        openWs();

        return () => {
            window.removeEventListener("click", enable);
            socket?.onClose();
        };
    }, [dispatch, notify, userId]);

    return contextHolder;
};

const timeToRefresh = 1800000;
const refreshCheckInterval = 900000;

const App = () => {
    const dispatch = useDispatch();
    const userId = useSelector(selectUserId);
    const [sessionUserId, setSessionUserId] = useState('');

    useEffect(() => {
        let isActive = true;
        getCurrentUser()
            .then(() => {
                if (isActive) dispatch(loginActions.getUserRequest());
            })
            .catch(() => {
                // No stored session: public pages and the login form remain available.
            });
        return () => { isActive = false; };
    }, [dispatch]);

    useEffect(() => {
        if (!userId) return;
        let isActive = true;

        const refreshSession = async () => {
            try {
                const { tokens } = await fetchAuthSession();
                if (!isActive) return;
                const expiresAt = tokens?.idToken?.payload.exp;
                if (!expiresAt) throw new Error("Missing authenticated session");
                if (expiresAt * 1000 - Date.now() < timeToRefresh) {
                    const session = await fetchAuthSession({ forceRefresh: true });
                    if (!session.tokens?.idToken) throw new Error("Missing refreshed session");
                }
                if (isActive) setSessionUserId(userId);
            } catch (error) {
                if (!isActive) return;
                dispatch(loginActions.logout());
                showErrorResponse("Unable to refresh Token", error);
            }
        };

        void refreshSession();
        const interval = setInterval(refreshSession, refreshCheckInterval);
        return () => {
            isActive = false;
            clearInterval(interval);
        };
    }, [dispatch, userId]);

    return (
        <>
            {userId && sessionUserId === userId && <InitPage />}

            <RouterProvider
                future={{
                    v7_startTransition: true,
                }}
                router={router}
            />
        </>
    );
};

export default App;
